import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, ChevronLeft } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  useCarousel,
} from "src/v1/ui/carousel/carousel";
import { LAWYERS, type Lawyer } from "src/constants/lawyers";

export const LawyersSection = () => {
  return (
    <section className="py-[80px] desktop:py-40 px-[16px] tablet:px-[32px] desktop:px-0 max-w-[1150px] mx-auto bg-[#F6F6F9] tablet:bg-white overflow-x-clip">
      {/* Header */}
      <div className="flex flex-col gap-5 items-center">
        <h2 className="text-heading-1-mb desktop:text-heading-1 text-[#212121]">
          수임료 지원으로 만나는 <br className="tablet:hidden" />
          검증된 엘리트 변호인단
        </h2>
        <p className="text-body-1-mb desktop:text-body-1 text-center text-[#4F4F4F]">
          채권도시의 수임료 지원제도로 채권추심, 소송, 강제집행 등 통합 법률
          서비스를 제공합니다.
        </p>
      </div>

      {/* Mobile: Carousel */}
      <div className="tablet:hidden [&_[data-slot=carousel-content]]:overflow-visible">
        <Carousel
          opts={{
            align: "start",
            loop: true,
          }}
          className="w-full mt-[20px]"
        >
          <CarouselNavigation />

          <CarouselContent>
            {LAWYERS.map((lawyer, index) => (
              <CarouselItem key={index} className="basis-[46%]">
                <LawyerCard lawyer={lawyer} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>

      {/* Tablet+: Grid */}
      <div className="hidden tablet:grid tablet:grid-cols-3 tablet:gap-5 desktop:gap-6 mt-10 gap-y-10">
        {LAWYERS.map((lawyer, index) => (
          <LawyerCard key={index} lawyer={lawyer} />
        ))}
      </div>

      {/* CTA Button */}
      <div className="mt-8 flex justify-center">
        <Link
          to="/company"
          className="group px-6 py-3 text-body-1 text-[#5B5F63] rounded-[24px] flex items-center gap-2 border border-[#5B5F63] transition-colors duration-300 hover:text-[#212121] hover:border-[#212121]"
        >
          변호사 소개
          <ArrowRight
            className="size-5 transition-transform duration-300 group-hover:translate-x-1"
            strokeWidth={1.5}
          />
        </Link>
      </div>
    </section>
  );
};

const CarouselNavigation = () => {
  const { scrollPrev, scrollNext } = useCarousel();

  return (
    <div className="flex justify-between mb-[20px]">
      <button
        onClick={scrollPrev}
        className="p-2 bg-[#D6DCFF] rounded-full"
        aria-label="Previous slide"
      >
        <ChevronLeft className="text-[#005CE2] size-6" strokeWidth={1.5} />
      </button>
      <button
        onClick={scrollNext}
        className="p-2 bg-[#D6DCFF] rounded-full"
        aria-label="Next slide"
      >
        <ChevronRight className="text-[#005CE2] size-6" strokeWidth={1.5} />
      </button>
    </div>
  );
};

const LawyerCard = ({ lawyer }: { lawyer: Lawyer }) => {
  return (
    <Link to={`/company/lawyers/${lawyer.slug}`} className="block">
      <article className="flex flex-col bg-[#F8F9FF] tablet:bg-[#F8F9FF] rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-[0_4px_6px_rgba(0,0,0,0.07),0_10px_24px_rgba(0,0,0,0.12)] hover:-translate-y-1 cursor-pointer">
        {/* Image */}
        <figure className="relative aspect-square">
          <img
            src={`/lawyer-${lawyer.slug}.webp`}
            alt={`${lawyer.name} ${lawyer.title}`}
            loading="lazy"
            decoding="async"
            width={300}
            height={300}
            className="w-full h-full object-cover"
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
          {/* Name overlay */}
          <figcaption className="absolute bottom-0 left-0 right-0 p-2 z-10">
            <h3 className="text-white flex gap-2 items-center">
              <span className="text-heading-3-mb desktop:text-heading-3">
                {lawyer.name}
              </span>
              <span className="text-body-2-mb desktop:text-body-1">
                {lawyer.title}
              </span>
            </h3>
          </figcaption>
        </figure>

        {/* Info */}
        <div className="p-2">
          <div className="flex flex-col gap-2">
            <div className="py-1 px-2 bg-c-primary text-white rounded-[4px] text-body-3-mb desktop:text-body-2 w-fit whitespace-nowrap">
              {lawyer.tag}
            </div>
            <ul className="mb-[14px] p-2">
              <div className="text-caption-2-mb desktop:text-caption-2 text-[#69769F]">
                경력
              </div>
              {lawyer.descriptions.map((desc, index) => (
                <li
                  key={index}
                  className="text-body-3-mb tablet:text-body-3 font-normal text-[#616269] whitespace-nowrap"
                >
                  {desc}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </article>
    </Link>
  );
};
